import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import useFetchDeclarations from "../hooks/useFetchDeclarations";
import useFetchSites from "../hooks/useFetchSites";
import useUsers from "../hooks/useUsers";
import { useEnums } from "../contexts/EnumContext";

import CustomSelect from "../utils/CustomSelect";

import SectionCard from "../utils/SectionCard";

import TextAreaEditor from "../utils/TextAreaEditor";

const Field = ({ label, children, required, mono }) => (
    <label className="block">
        <span className="text-gray-700 text-sm">
            {label} {required && <span className="text-red-500">*</span>}
        </span>
        <div className={`${mono ? "font-mono" : ""} mt-1`}>{children}</div>
    </label>
);

const RefRowEdit = ({ label, societe, setSociete, nom, setNom, refcode, setRefcode }) => (
    <div className="items-center gap-4 grid grid-cols-12 py-2 border-b last:border-b-0">
        <div className="col-span-12 sm:col-span-3">
            <div className="font-medium text-gray-600 text-sm">{label}</div>
        </div>
        <div className="col-span-12 sm:col-span-3">
            <input
                className="px-3 py-2 border border-gray-300 rounded w-full"
                value={societe}
                onChange={(e) => setSociete(e.target.value)}
                placeholder="Société"
            />
        </div>
        <div className="col-span-12 sm:col-span-3">
            <input
                className="px-3 py-2 border border-gray-300 rounded w-full"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Nom & Prénom"
            />
        </div>
        <div className="col-span-12 sm:col-span-3">
            <input
                className="px-3 py-2 border border-gray-300 rounded w-full font-mono"
                value={refcode}
                onChange={(e) => setRefcode(e.target.value)}
                placeholder="Référence"
            />
        </div>
    </div>
);

const isZeroDate = (s) => !s || s === "0000-00-00";
const toInputDate = (s) => (isZeroDate(s) ? "" : s?.slice(0, 10) || "");
const fromInputDate = (s) => (s ? s : "0000-00-00");

export default function DeclarationCreatePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { createDeclaration, loading } = useFetchDeclarations();

    const siteUUIDLocation = location.state?.siteUUID || "";

    const { sites, fetchSites, loading: loadingSites, error: errorSites } = useFetchSites();
    const { users, fetchUsers } = useUsers();
    const { enums } = useEnums();


    useEffect(() => {
        fetchSites();
        fetchUsers();
    }, []);

    // ------------ état minimal pour tous les champs du détail -------------
    const [decla, setDecla] = useState({
        UUID_Site_: "",
        SiteLabel: "",

        Libelle: "",
        Type_Declaration: "",
        Etat: enums[`Statut déclaration`]?.[0] ?? "",
        Etat_Motif: "",
        Origine_de_la_demande: "",
        Demande_Revue_Par: "",
        Charge_affaire: "",
        Date_Sinistre: "0000-00-00",
        Date_Declaration: "0000-00-00",

        Expert_Societe: "",
        Expert_Nom: "",
        Expert_Ref: "",
        Assureur_Client_Nom: "",
        Assureur_Client_Nom2: "",
        Assureur_Client_Ref: "",
        Courtier_Nom: "",
        Courtier_Nom2: "",
        Courtier_Ref: "",
        Assureur_Interne_Nom: "",
        Assureur_Interne_Nom2: "",
        Assureur_Interne_Ref: "",

        // --- LÉSÉ / GPA ---
        GPA_Type_Declarant: "",
        GPA_OS: "",
        GPA_Locataire: "",
        GPA_Prenom: "",
        GPA_Societe: "",
        GPA_Adresse: "",
        GPA_App_Etage: "",
        GPA_Locataire_Tel: "",
        GPA_Email: "",
        GPA_Demande_Par: "",
        GPA_Intervenant: "",
        GPA_Date_RDV: "0000-00-00",
        GPA_DateLimite: "0000-00-00",
        GPA_NB_Jour_Inter: "",
        GPA_Mise_en_demeure: "0000-00-00",
        GPA_Num_Logement: "",
        GPA_Relance_SousTraitant: "",
        GPA_Num_Batiment: "",
        GPA_Courrier_Locataire: "",

        // --- JURIDIQUE ---
        Jur_Juriste: "",
        Jur_instance: "",
        Jur_Num_Dossier_Avocat: "",
        Jur_Avocat: "",
        Jur_Parties: "",
        Jur_Suivi_Tech: "",
        Jur_Evaluation: "",
        Jur_Reel: "",
        Jur_Ref_partie_adverse: "",
        Jur_Date_Jugement: "0000-00-00",
        Jur_Date_Assignation: "0000-00-00",
        Jur_Ref_expert_adverse: "",
        Jur_Ref_partie_adverse: "",

        // --- Récapitulatif financier
        Cout_tt_Pathologie: "",
        Montant_Du_Definitif: "",

        // --- Remarques
        Remarques: "",

        // --- Commentaires
       // Commentaire_libelle: "",
        //Commentaire_texte: "",
    });

    const [errors, setErrors] = useState({});
    const set = (name, value) => setDecla((s) => ({ ...s, [name]: value }));

    const cmpFR = (a = "", b = "") =>
        a.localeCompare(b, "fr", { sensitivity: "base" });

    const usersCAOptions = (users ?? [])
        .filter(u => !!u?.Charge_Affaire)
        .reduce((acc, u) => {
            const key = u?.UUID_ ?? `${u?.Nom}-${u?.Prenom}`;
            if (!acc.map.has(key)) {
                acc.map.set(key, u);
                acc.list.push(u);
            }
            return acc;
        }, { map: new Map(), list: [] }).list
        .sort((a, b) => cmpFR(a?.Nom, b?.Nom) || cmpFR(a?.Prenom, b?.Prenom))
        .map(u => ({
            value: u?.UUID_,
            label: `${u?.Nom ?? ""}`.trim()
        }));

    console.log("usersCAOptions", usersCAOptions);

    const validate = () => {
        const e = {};
        if (!decla.Libelle?.trim()) e.Libelle = "Libellé requis";
        if (!decla.Type_Declaration?.trim()) e.Type_Declaration = "Type requis";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSave = async () => {
        if (!validate()) return;

        const {
            SiteLabel,
            ...declaSansSiteLabel
        } = decla;

        const payload = {
            ...declaSansSiteLabel,
            Date_Sinistre: fromInputDate(toInputDate(decla.Date_Sinistre)),

            Cout_tt_Pathologie: decla.Cout_tt_Pathologie === "" ? 0 : Number(decla.Cout_tt_Pathologie),
            Montant_Du_Definitif: decla.Montant_Du_Definitif === "" ? 0 : Number(decla.Montant_Du_Definitif),
        };

        const created = await createDeclaration(payload);
        if (created?.UUID_) {
            navigate(`/declarations/${created.UUID_}`);
        } else {
            navigate(`/declarations`);
        }
    };

    const siteOptions = useMemo(() => {
        if (!Array.isArray(sites)) return [];
        return sites.map((s) => ({
            id: s.UUID_,
            label: s.Nom || "Site sans nom",
        }));
    }, [sites]);

    useEffect(() => {
        if (!siteUUIDLocation || !sites?.length) return;
        const found = sites.find(s => s.UUID_ === siteUUIDLocation);
        if (found) {
            setDecla(d => ({ ...d, UUID_Site_: found.UUID_, SiteLabel: found.Nom || "" }));
        }
    }, [siteUUIDLocation, sites]);

    const lockSite = Boolean(siteUUIDLocation);

    console.log(lockSite);

    const typeDeclOptions = [
        "Sinistre",
        "Réclamation",
        "DoP / GPA",
        "Autre",
    ];
    const etatOptions = ["OUVERT", "EN COURS", "CLOS", "ARCHIVÉ"];

    return (
        <div className="mx-auto p-4 lg:p-8">
            <div className="top-4 lg:top-8 z-20 sticky flex justify-center items-center bg-gray-900 drop-shadow-xl mb-6 px-8 rounded-lg w-full min-h-20">
                <div className="flex justify-between items-center px-4 md:px-0 py-3 w-full h-full">

                    <Link
                        to={siteUUIDLocation ? `/sites/${siteUUIDLocation}` : "/declarations"}
                        className="inline-flex items-center gap-2 text-white hover:text-orange-500 transition duration-300 ease-in-out"
                    >
                        <i className="fa-arrow-left fa-solid" />
                        {siteUUIDLocation ? (<span>Revenir aux Déclarations du Site</span>) : (<span>Revenir à la liste des Déclarations</span>)}
                    </Link>
                    <button
                        onClick={onSave}
                        disabled={loading}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white 
                        ${loading ? "bg-orange-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-500 transition duration-300 ease-in-out cursor-pointer"}`}
                    >
                        <i className="fa-solid fa-floppy-disk" />
                        <span>Sauvegarder la déclaration</span>
                    </button>
                </div>
            </div>

            {/* bandeau titre */}
            <div className="flex items-end gap-4 bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 mt-4 p-4 px-6 rounded-lg text-white">
                <p className="space-x-2 font-bold text-2xl">
                    <i className="fa-solid fa-file-shield fa-sm" />
                    <span>{decla.Libelle || "Création d'une nouvelle Déclaration"}</span>
                </p>
                {decla.Code_Procedure ? (
                    <p className="space-x-2 font-medium text-sm">
                        <i className="fa-solid fa-hashtag" />
                        <span className="uppercase">{decla.Code_Procedure}</span>
                        {decla.Type_Declaration && <span className="font-bold">({decla.Type_Declaration})</span>}
                    </p>
                ) : null}
            </div>

            <div className="space-y-6 mt-6">
                {/* BLOC 1 — Informations générales */}
                <SectionCard title="Informations générales" icon="fa-circle-info">
                    <div className="gap-4 grid grid-cols-1 md:grid-cols-2">
                        <Field label="Site" required>
                            <div className="relative">
                                <CustomSelect
                                    isPortal
                                    options={siteOptions}
                                    isNested
                                    isDisabled={lockSite}
                                    value={decla.SiteLabel || (loadingSites ? "Chargement..." : "Sélectionner…")}
                                    onChange={(opt) => {
                                        set("UUID_Site_", opt?.id || "");
                                        set("SiteLabel", opt?.label || "");
                                    }}
                                    className={`w-full ${errors.UUID_Site_ ? "border-red-400" : ""} ${siteUUIDLocation ? "bg-orange-100" : ""}`}
                                />
                                {errors.UUID_Site_ && <p className="mt-1 text-red-500 text-xs">{errors.UUID_Site_}</p>}
                                {errorSites && <p className="mt-1 text-red-500 text-xs">Erreur de chargement des sites : {errorSites}</p>}
                            </div>
                        </Field>

                        <Field label="Libellé" required>
                            <input
                                className={`px-3 py-2 border rounded w-full ${errors.Libelle ? "border-red-400" : "border-gray-300"}`}
                                value={decla.Libelle}
                                onChange={(e) => set("Libelle", e.target.value)}
                                placeholder="Ex: Infiltration toiture bâtiment B"
                            />
                            {errors.Libelle && <p className="mt-1 text-red-500 text-xs">{errors.Libelle}</p>}
                        </Field>

                        <Field label="Type de déclaration" required>
                            <CustomSelect
                                isPortal
                                options={enums[`TypesDeclas`] ?? []}
                                value={decla.Type_Declaration || "Sélectionner…"}
                                onChange={(val) => set("Type_Declaration", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                            {errors.Type_Declaration && <p className="mt-1 text-red-500 text-xs">{errors.Type_Declaration}</p>}
                        </Field>

                        <Field label="État">
                            <CustomSelect
                                isPortal
                                options={enums[`Statut déclaration`] ?? []}
                                value={decla.Etat || "Sélectionner…"}
                                onChange={(val) => set("Etat", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                        </Field>

                        <Field label="État Motif">
                            <CustomSelect
                                isPortal
                                options={enums[`Motif_Etat_Declaration`] ?? []}
                                value={decla.Etat_Motif || "Sélectionner…"}
                                onChange={(val) => set("Etat_Motif", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                        </Field>

                        <Field label="Origine de la demande">
                            <CustomSelect
                                isPortal
                                options={enums[`Déclaration Origine`] ?? []}
                                value={decla.Origine_de_la_demande || "Sélectionner…"}
                                onChange={(val) => set("Origine_de_la_demande", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                        </Field>

                        <Field label="Demande de revue par">
                            <CustomSelect
                                isPortal
                                isNested
                                options={usersCAOptions ?? []}
                                value={decla.Demande_Revue_Par || "Sélectionner…"}
                                onChange={(val) => set("Demande_Revue_Par", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                        </Field>

                        <Field label="Suivi par">
                            <CustomSelect
                                isPortal
                                isNested
                                options={usersCAOptions ?? []}
                                value={decla.Charge_affaire || "Sélectionner…"}
                                onChange={(val) => set("Charge_affaire", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                        </Field>

                        <Field label="Date de sinistre">
                            <input
                                type="date"
                                className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={toInputDate(decla.Date_Sinistre)}
                                onChange={(e) => set("Date_Sinistre", fromInputDate(e.target.value))}
                            />
                        </Field>

                        <Field label="Date de déclaration">
                            <input
                                type="date"
                                className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={toInputDate(decla.Date_Declaration)}
                                onChange={(e) => set("Date_Declaration", fromInputDate(e.target.value))}
                            />
                        </Field>
                    </div>
                </SectionCard>

                <SectionCard title="Références" icon="fa-address-book">
                    <div className="hidden gap-4 sm:grid grid-cols-12 mb-1 text-gray-500 text-xs">
                        <div className="col-span-3"></div>
                        <div className="col-span-3 text-center">Société</div>
                        <div className="col-span-3 text-center">Nom & Prénom</div>
                        <div className="col-span-3 text-center">Référence</div>
                    </div>

                    <RefRowEdit
                        label="Expert"
                        societe={decla.Expert_Societe}
                        setSociete={(v) => set("Expert_Societe", v)}
                        nom={decla.Expert_Nom}
                        setNom={(v) => set("Expert_Nom", v)}
                        refcode={decla.Expert_Ref}
                        setRefcode={(v) => set("Expert_Ref", v)}
                    />

                    <RefRowEdit
                        label="Assureur Client"
                        societe={decla.Assureur_Client_Nom}
                        setSociete={(v) => set("Assureur_Client_Nom", v)}
                        nom={decla.Assureur_Client_Nom2}
                        setNom={(v) => set("Assureur_Client_Nom2", v)}
                        refcode={decla.Assureur_Client_Ref}
                        setRefcode={(v) => set("Assureur_Client_Ref", v)}
                    />

                    <RefRowEdit
                        label="Courtier"
                        societe={decla.Courtier_Nom}
                        setSociete={(v) => set("Courtier_Nom", v)}
                        nom={decla.Courtier_Nom2}
                        setNom={(v) => set("Courtier_Nom2", v)}
                        refcode={decla.Courtier_Ref}
                        setRefcode={(v) => set("Courtier_Ref", v)}
                    />

                    <RefRowEdit
                        label="Assureur Interne"
                        societe={decla.Assureur_Interne_Nom}
                        setSociete={(v) => set("Assureur_Interne_Nom", v)}
                        nom={decla.Assureur_Interne_Nom2}
                        setNom={(v) => set("Assureur_Interne_Nom2", v)}
                        refcode={decla.Assureur_Interne_Ref}
                        setRefcode={(v) => set("Assureur_Interne_Ref", v)}
                    />
                </SectionCard>

                {/* LÉSÉ / GPA */}
                <SectionCard title="Lésé / GPA" icon="fa-user">
                    <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
                        {/* Colonne gauche */}
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Catégorie">
                              <CustomSelect
                                options={enums[`Declaration_GPA_Type_Declarant`] ?? []}
                                value={decla.GPA_Type_Declarant || "Sélectionner…"}
                                onChange={(val) => set("GPA_Type_Declarant", typeof val === "string" ? val : val?.label || "")}
                                className="w-full"
                            />
                            </Field>
                            <Field label="OS">
                                <input
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_OS}
                                    onChange={(e) => set("GPA_OS", e.target.value)}
                                />
                            </Field>
                            <Field label="Nom">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Locataire}
                                    onChange={(e) => set("GPA_Locataire", e.target.value)} />
                            </Field>
                            <Field label="Prénom">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Prenom}
                                    onChange={(e) => set("GPA_Prenom", e.target.value)} />
                            </Field>
                            <Field label="Société">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Societe}
                                    onChange={(e) => set("GPA_Societe", e.target.value)} />
                            </Field>
                            <Field label="Adresse">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Adresse}
                                    onChange={(e) => set("GPA_Adresse", e.target.value)} />
                            </Field>
                            <Field label="Complément (app/étage)">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_App_Etage}
                                    onChange={(e) => set("GPA_App_Etage", e.target.value)} />
                            </Field>
                            <Field label="Téléphone">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Locataire_Tel}
                                    onChange={(e) => set("GPA_Locataire_Tel", e.target.value)} />
                            </Field>
                            <Field label="Email">
                                <input type="email" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Email}
                                    onChange={(e) => set("GPA_Email", e.target.value)} />
                            </Field>
                        </div>

                        {/* Colonne droite */}
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Saisie par">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Demande_Par}
                                    onChange={(e) => set("GPA_Demande_Par", e.target.value)} />
                            </Field>
                            <Field label="Entreprise">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Intervenant}
                                    onChange={(e) => set("GPA_Intervenant", e.target.value)} />
                            </Field>
                            <Field label="Date RDV">
                                <input type="date" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(decla.GPA_Date_RDV)}
                                    onChange={(e) => set("GPA_Date_RDV", fromInputDate(e.target.value))} />
                            </Field>
                            <Field label="Date Limite">
                                <input type="date" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(decla.GPA_DateLimite)}
                                    onChange={(e) => set("GPA_DateLimite", fromInputDate(e.target.value))} />
                            </Field>
                            <Field label="Sous un délai de">
                                <input
                                    type="number"
                                    step="1"
                                    className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_NB_Jour_Inter}
                                    onChange={(e) => set("GPA_NB_Jour_Inter", e.target.value)} />
                            </Field>
                            <Field label="Mise en demeure">
                                <input type="date" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(decla.GPA_Mise_en_demeure)}
                                    onChange={(e) => set("GPA_Mise_en_demeure", fromInputDate(e.target.value))} />
                            </Field>
                            <Field label="Logement">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    type="text"
                                    value={decla.GPA_Num_Logement}
                                    onChange={(e) => set("GPA_Num_Logement", e.target.value)} />
                            </Field>
                            <Field label="Relance sous-traitant">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Relance_SousTraitant}
                                    onChange={(e) => set("GPA_Relance_SousTraitant", e.target.value)} />
                            </Field>
                            <Field label="Bâtiment">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    type="text"
                                    value={decla.GPA_Num_Batiment}
                                    onChange={(e) => set("GPA_Num_Batiment", e.target.value)} />
                            </Field>
                            <Field label="Courrier locataire">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.GPA_Courrier_Locataire}
                                    onChange={(e) => set("GPA_Courrier_Locataire", e.target.value)} />
                            </Field>
                        </div>
                    </div>
                </SectionCard>

                {/* JURIDIQUE */}
                <SectionCard title="Juridique" icon="fa-scale-balanced">
                    <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
                        {/* Colonne gauche */}
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Juriste">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Juriste}
                                    onChange={(e) => set("Jur_Juriste", e.target.value)} />
                            </Field>
                            <Field label="Instances">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_instance}
                                    onChange={(e) => set("Jur_instance", e.target.value)} />
                            </Field>
                            <Field label="Num DOSSIER">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full font-mono"
                                    value={decla.Jur_Num_Dossier_Avocat}
                                    onChange={(e) => set("Jur_Num_Dossier_Avocat", e.target.value)} />
                            </Field>
                            <Field label="Avocat">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Avocat}
                                    onChange={(e) => set("Jur_Avocat", e.target.value)} />
                            </Field>
                            <Field label="Parties">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Parties}
                                    onChange={(e) => set("Jur_Parties", e.target.value)} />
                            </Field>
                            <Field label="Suivi Tech">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Suivi_Tech}
                                    onChange={(e) => set("Jur_Suivi_Tech", e.target.value)} />
                            </Field>
                            <Field label="Prétention">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Pretention}
                                    onChange={(e) => set("Jur_Pretention", e.target.value)} />
                            </Field>
                            <Field label="Estimation">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Evaluation}
                                    onChange={(e) => set("Jur_Evaluation", e.target.value)} />
                            </Field>
                            <Field label="Montant (€)">
                                <input type="number" step="1" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Reel}
                                    onChange={(e) => set("Jur_Reel", e.target.value)} />
                            </Field>
                            <Field label="Réf. partie adverse">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Ref_partie_adverse}
                                    onChange={(e) => set("Jur_Ref_partie_adverse", e.target.value)} />
                            </Field>
                        </div>

                        {/* Colonne droite */}
                        <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                            <Field label="Date Jugement">
                                <input type="date" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(decla.Jur_Date_Jugement)}
                                    onChange={(e) => set("Jur_Date_Jugement", fromInputDate(e.target.value))} />
                            </Field>
                            <Field label="Date Assignation">
                                <input type="date" className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={toInputDate(decla.Jur_Date_Assignation)}
                                    onChange={(e) => set("Jur_Date_Assignation", fromInputDate(e.target.value))} />
                            </Field>
                            <Field label="Réf expert adverse">
                                <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                    value={decla.Jur_Ref_expert_adverse}
                                    onChange={(e) => set("Jur_Ref_expert_adverse", e.target.value)} />
                            </Field>
                            <div className="hidden sm:block" />
                            <div className="hidden sm:block" />
                        </div>
                    </div>
                </SectionCard>

                {/* REMARQUES */}
                <SectionCard title="Remarques" icon="fa-note-sticky">
                    <div className="space-y-1">
                        <div className="text-gray-500 text-xs">Remarques</div>
                        <TextAreaEditor
                            value={decla.Remarques}
                            onChange={(e) => set("Remarques", e.target.value)}
                            placeholder="Saisir les Remarques"
                        />
                    </div>
                </SectionCard>

                {/* COMMENTAIRE */}
               {/* <SectionCard title="Commentaires" icon="fa-note-sticky">
                    <div className="space-y-1">
                        <Field label="Libelle ">
                            <input className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={decla.Commentaire_libelle}
                                onChange={(e) => set("Commentaire_libelle", e.target.value)} />
                        </Field>
                        <div className="text-gray-500 text-xs">Commentaire</div>

                        <TextAreaEditor
                            value={decla.Commentaire_texte}
                            onChange={(e) => set("Commentaire_texte", e.target.value)}
                            placeholder="Saisir les commentaires"
                        />
                    </div>
                </SectionCard>*/}

                {/* BLOC 3 — Récapitulatif financier */}
                <SectionCard title="Récapitulatif financier" icon="fa-file-invoice-dollar">
                    <div className="gap-4 grid grid-cols-1 sm:grid-cols-2">
                        <Field label="Coût Pathologie (€)">
                            <input
                                type="number"
                                step="1"
                                className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={decla.Cout_tt_Pathologie}
                                onChange={(e) => set("Cout_tt_Pathologie", e.target.value)}
                            />
                        </Field>
                        <Field label="Coût Déclaration (€)">
                            <input
                                type="number"
                                step="1"
                                className="px-3 py-2 border border-gray-300 rounded w-full"
                                value={decla.Montant_Du_Definitif}
                                onChange={(e) => set("Montant_Du_Definitif", e.target.value)}
                            />
                        </Field>
                    </div>
                </SectionCard>
            </div>
        </div>
    );
}
